// Single source of truth for every third-party integration the app supports.
// Each entry drives both the Integrations Hub UI and where the integration
// surfaces inside the app (notifications, calendar, migration, etc.).
//
// To add a new integration: append an entry here. The Hub renders it
// automatically. Capability hooks pick it up in phase 3 of the rollout.

import { MIGRATION_SOURCES } from '@/data/migrationSources';

/**
 * Canonical deep link to Work Calendar Settings → Calendar Subscription.
 * Index.tsx consumes `?settings=<tab>&section=<section>` and routes it through
 * the established `workitect-open-settings-tab` settings-opening architecture.
 */
export const CALENDAR_SUBSCRIPTION_HREF =
  '/app?settings=user&section=calendar-subscription';


export type IntegrationCategory =
  | 'Productivity'
  | 'Communication'
  | 'Storage'
  | 'Calendar'
  | 'Email'
  | 'AI'
  | 'Analytics'
  | 'Migration'
  | 'Other';

export type IntegrationCapability =
  | 'notify'
  | 'calendar'
  | 'storage'
  | 'sync-tasks'
  | 'migration'
  | 'ai'
  | 'email';

export type IntegrationScope = 'workspace' | 'personal' | 'both';

export type IntegrationConnector =
  | { kind: 'standard'; connectorId: string }
  | { kind: 'oauth'; startFn: string; callbackFn: string }
  | { kind: 'apiKey'; secretName: string; helpUrl?: string }
  | { kind: 'native' /* implemented in-app, e.g. ICS calendar feed */ }
  | { kind: 'platform' /* platform-configured server service — nothing to connect */ };


export interface IntegrationDefinition {
  id: string;
  name: string;
  category: IntegrationCategory;
  description: string;
  logoUrl?: string;
  emoji?: string;
  scope: IntegrationScope;
  connector: IntegrationConnector;
  capabilities: IntegrationCapability[];
  /** If true, surfaced in the Browse tab but not yet enabled — shows "Coming soon". */
  comingSoon?: boolean;
  /**
   * Platform-configured server service. Not a user or workspace connection:
   * no connect/manage/disconnect controls, no secret identifiers, and it is
   * never reported as "Connected".
   */
  platformManaged?: boolean;
  /** Optional deep-link to where the integration is configured today. */
  manageHref?: string;
  /**
   * True ONLY when Workitect holds a verifiable, provider-authenticated
   * connection for this entry (server-side credential bound to a user /
   * instance). No current entry qualifies: label-only rows, secret presence
   * and app-login identity links are never a connection. Presentation ignores
   * every "connected" claim for entries without this flag.
   */
  verifiedConnection?: boolean;
}


// Migration sources auto-projected from the existing registry so the
// migration page and the Hub stay in sync.
const migrationIntegrations: IntegrationDefinition[] = MIGRATION_SOURCES
  .filter((s) => s.id !== 'generic')
  .map<IntegrationDefinition>((src) => ({
    id: `migration-${src.id}`,
    name: src.name,
    category: 'Migration',
    description: `Bring your ${src.name} projects, tasks, and assignees into Workitect.`,
    logoUrl: src.logoUrl,
    emoji: src.emoji,
    scope: 'workspace',
    connector: { kind: 'native' },
    capabilities: ['migration'],
    manageHref: '/app/migrate',
  }));

// First-party / already-wired integrations.
//
// NOTE: there is no "Wrike (live sync)" entry. Workitect does not provide
// continuous Wrike task sync. The supported Wrike surface is the Wrike
// Migration card (projected from MIGRATION_SOURCES below).
const firstPartyIntegrations: IntegrationDefinition[] = [
  {
    id: 'calendar-ics',
    name: 'Calendar (iCal subscribe)',
    category: 'Calendar',
    description:
      'Subscribe to a live updating .ics feed from Google Calendar, Outlook, Apple Calendar, or any iCal-compatible client.',
    emoji: '📅',
    scope: 'personal',
    connector: { kind: 'native' },
    capabilities: ['calendar'],
    manageHref: CALENDAR_SUBSCRIPTION_HREF,
  },
  {
    id: 'elevenlabs',
    name: 'ElevenLabs',
    category: 'AI',
    description:
      'Voice narration and meeting transcription are provided by the platform for Note-to-Task and approvals. Nothing to connect.',
    emoji: '🎙️',
    scope: 'workspace',
    connector: { kind: 'platform' },
    capabilities: ['ai'],
    platformManaged: true,
  },
  {
    id: 'resend',
    name: 'Resend',
    category: 'Email',
    description:
      'Outbound email for digests, approvals, and notifications is delivered by the platform. Nothing to connect.',
    emoji: '✉️',
    scope: 'workspace',
    connector: { kind: 'platform' },
    capabilities: ['email', 'notify'],
    platformManaged: true,
  },
];


// Standard-connector tiles surfaced in Browse. Marked comingSoon until the
// Hub wires the connect/disconnect flow in phase 2 of the rollout.
const standardConnectorIntegrations: IntegrationDefinition[] = [
  {
    id: 'slack',
    name: 'Slack',
    category: 'Communication',
    description: 'Send digests, approval pings, and stall alerts to a Slack channel.',
    emoji: '💬',
    scope: 'workspace',
    connector: { kind: 'standard', connectorId: 'slack' },
    capabilities: ['notify'],
    comingSoon: true,
  },
  {
    id: 'microsoft-teams',
    name: 'Microsoft Teams',
    category: 'Communication',
    description: 'Post updates and approvals into a Teams channel.',
    emoji: '🟦',
    scope: 'workspace',
    connector: { kind: 'standard', connectorId: 'microsoft-teams' },
    capabilities: ['notify'],
    comingSoon: true,
  },
  {
    id: 'google-calendar',
    name: 'Google Calendar',
    category: 'Calendar',
    description: 'Two-way sync between Workitect tasks and your Google Calendar events.',
    emoji: '📆',
    scope: 'personal',
    connector: { kind: 'standard', connectorId: 'google-calendar' },
    capabilities: ['calendar'],
    comingSoon: true,
  },
  {
    id: 'google-drive',
    name: 'Google Drive',
    category: 'Storage',
    description: 'Attach Drive files to tasks without re-uploading.',
    emoji: '🗂️',
    scope: 'workspace',
    connector: { kind: 'standard', connectorId: 'google-drive' },
    capabilities: ['storage'],
    comingSoon: true,
  },
  {
    id: 'gmail',
    name: 'Gmail',
    category: 'Email',
    description: 'Turn starred Gmail threads into tasks with one click.',
    emoji: '📧',
    scope: 'personal',
    connector: { kind: 'standard', connectorId: 'gmail' },
    capabilities: ['email'],
    comingSoon: true,
  },
  {
    id: 'notion',
    name: 'Notion',
    category: 'Productivity',
    description: 'Mirror Notion databases as Workitect projects or tasks.',
    emoji: '⬛',
    scope: 'workspace',
    connector: { kind: 'standard', connectorId: 'notion' },
    capabilities: ['sync-tasks'],
    comingSoon: true,
  },
  {
    id: 'linear',
    name: 'Linear',
    category: 'Productivity',
    description: 'Link Linear issues to tasks and keep their status aligned.',
    emoji: '📐',
    scope: 'workspace',
    connector: { kind: 'standard', connectorId: 'linear' },
    capabilities: ['sync-tasks'],
    comingSoon: true,
  },
  {
    id: 'hubspot',
    name: 'HubSpot',
    category: 'Productivity',
    description: 'Pull HubSpot deals or contacts into intake forms and project plans.',
    emoji: '🟧',
    scope: 'workspace',
    connector: { kind: 'standard', connectorId: 'hubspot' },
    capabilities: ['sync-tasks'],
    comingSoon: true,
  },
];

export const INTEGRATIONS: IntegrationDefinition[] = [
  ...firstPartyIntegrations,
  ...standardConnectorIntegrations,
  ...migrationIntegrations,
];

export const INTEGRATION_CATEGORIES: IntegrationCategory[] = [
  'Productivity',
  'Communication',
  'Calendar',
  'Storage',
  'Email',
  'AI',
  'Analytics',
  'Migration',
  'Other',
];

export const getIntegration = (id: string): IntegrationDefinition | undefined =>
  INTEGRATIONS.find((i) => i.id === id);

export const getIntegrationsByCapability = (
  capability: IntegrationCapability
): IntegrationDefinition[] => INTEGRATIONS.filter((i) => i.capabilities.includes(capability));
